import { ChangeEvent, DragEvent, useRef, useState } from 'react';

import styled from 'styled-components';

import { FormLabelContainer, FormLabelContainerProps } from '@components/form-label/form-label';
import { IconCheckmark } from '@components/icons/checkmark';
import { ProgressBar } from '@components/progress-bar';
import { Bar } from '@components/progress-bar/progress-bar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { showError } from '@components/toaster';
import { VisuallyHidden } from '@components/visually-hidden';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { equivalentExtensions, isFileNameAllowed, normalizeExtension } from '@utils/file-types';
import { transitionStandard } from '@utils/transitions';

export type FileDropzoneProps = FormLabelContainerProps & {
  handleUpload?(file: File, setProgress: (progress: number) => void): void;
  onChange?(file: File | null): void;
  onFinish?(file: File): void;
  fileTypes?: Array<string>;
  error?: string;
};

const FINISH_DELAY = 1000;

const defaultProgress = -1;

export const FileDropzone = ({
  handleUpload,
  onChange,
  onFinish,
  fileTypes = [],
  error,
  ...labelProps
}: FileDropzoneProps) => {
  const { t } = useScopedTranslate(locale);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState(defaultProgress);
  const [errorMessage, setErrorMessage] = useState('');
  const finalErrorMessage = error ?? errorMessage;
  const hasError = Boolean(finalErrorMessage);
  const hasFileTypeConstraints = fileTypes.length > 0;
  const isUploading = progress > defaultProgress;

  // Equivalent filetypes are accepted too, though only the configured ones are
  // displayed — `.jpg` and `.jpeg` are the same photo to the person uploading it.
  const allowedFileTypes = fileTypes.flatMap((fileType) =>
    equivalentExtensions(normalizeExtension(fileType)),
  );

  const getFileTypeSplit = () => {
    const parsedFileTypes = fileTypes.map((fileType) => `.${fileType}`);

    return {
      allButLastFileType: parsedFileTypes.slice(0, -1).join(', '),
      lastFileType: parsedFileTypes[parsedFileTypes.length - 1],
    };
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files[0]) {
      onFileSelect(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef?.current?.click();
  };

  const handleSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const handleFinish = (file: File) => {
    setTimeout(() => {
      setProgress(defaultProgress);
      onFinish?.(file);
    }, FINISH_DELAY);
  };

  const onFileSelect = async (file: File) => {
    if (!isUploading) {
      setErrorMessage('');
      if (hasFileTypeConstraints && !isFileNameAllowed(file.name, allowedFileTypes)) {
        setErrorMessage(t('fileTypeError'));
      } else if (handleUpload) {
        try {
          setProgress(0);
          await handleUpload(file, setProgress);
          handleFinish(file);
        } catch (e) {
          setProgress(defaultProgress);
          showError(t('uploadError'));
        }
      } else if (onChange) {
        onChange(file);
        onFinish?.(file);
      }
    }
  };

  return (
    <FormLabelContainer {...labelProps}>
      <DropContainer
        onDragEnter={handleDragEnter}
        onDragOver={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        $isUploading={isUploading}
        $isDragging={isDragging}
        $hasError={hasError}
      >
        <InstructionsContainer>
          {progress > defaultProgress ? (
            <Progress progress={progress} />
          ) : (
            <>
              <Text font="regular14" color="blue100" text={t('title')} />
              {hasError && <Text color="error" text={errorMessage} />}
              {hasFileTypeConstraints && (
                <Text
                  font="regular14"
                  color={hasError ? 'error' : 'textHelper'}
                  text={
                    fileTypes.length > 1
                      ? t('constraints', getFileTypeSplit())
                      : t('constraint', { fileType: `.${fileTypes[0]}` })
                  }
                />
              )}
            </>
          )}
          <VisuallyHidden>
            <input
              ref={fileInputRef}
              name="file"
              type="file"
              accept={allowedFileTypes.map((fileType) => `.${fileType}`).join(',')}
              onChange={handleSelect}
              disabled={isUploading}
            />
          </VisuallyHidden>
        </InstructionsContainer>
      </DropContainer>
    </FormLabelContainer>
  );
};

type ProgressType = {
  progress: number;
};

const Progress = ({ progress }: ProgressType) => {
  const { t } = useScopedTranslate(locale);
  const isFinished = progress >= 1;
  const title = isFinished ? t('uploaded') : t('uploading');
  return (
    <Stack space={1} align="center">
      <Stack orientation="horizontal" space={0}>
        {isFinished && <IconCheckmark color="green100" />}
        <Text color="textSecondary" text={title} />
      </Stack>
      <ProgressBarContainer $isFinished={isFinished}>
        <ProgressBar ratio={progress} />
      </ProgressBarContainer>
    </Stack>
  );
};

const ProgressBarContainer = styled.div<{ $isFinished: boolean }>(({ $isFinished }) => ({
  minWidth: pxToRem(184),
  [`${Bar}`]: $isFinished
    ? {}
    : {
        backgroundColor: theme.color.blue100,
      },
}));

const DropContainer = styled.div<{
  $isDragging: boolean;
  $hasError: boolean;
  $isUploading: boolean;
}>(({ $isDragging, $hasError, $isUploading }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: pxToRem(76),
    transition: transitionStandard('background', 'border-color'),
    '&:hover': {
      borderColor: theme.color.blue100,
      backgroundColor: theme.color.blueTrans5,
    },
    borderRadius: pxToRem(8),
    borderStyle: $isUploading ? 'solid' : 'dashed',
    borderWidth: pxToRem(1),
    borderColor: $isUploading
      ? theme.color.border10
      : $isDragging
        ? theme.color.blue100
        : $hasError
          ? theme.color.error
          : theme.color.border10,
    backgroundColor: $isUploading
      ? theme.color.uiTrans5
      : $isDragging
        ? theme.color.blueTrans5
        : $hasError
          ? theme.color.redTrans5
          : undefined,

    cursor: 'pointer',
    '*': {
      pointerEvents: 'none',
    },
  };
});

const InstructionsContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1,
  padding: pxToRem(16),
  textAlign: 'center',
});

const locale = {
  en: {
    title: 'Click to choose a file or drag and drop here',
    constraint: 'Accepted file types: {fileType}',
    constraints: 'Accepted file types: {allButLastFileType} and {lastFileType}',
    dragMessage: 'Please drop your file here',
    uploading: 'Uploading...',
    uploaded: 'Uploaded',
    uploadError: 'Failed to upload file, please try again or choose a different file',
    fileTypeError: 'Unsupported file format',
  },
};
